import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import CTA from '../components/sections/CTA';
import { expertService, publicService } from '../api/services';
import { Star, MapPin, Clock, AlertCircle, Users } from 'lucide-react';

const VIDEO_URL = 'https://firebasestorage.googleapis.com/v0/b/images-1aaab.appspot.com/o/20546634-uhd_3840_2160_30fps.mp4?alt=media&token=43e2bb92-2523-416f-a0b3-a1c42a351537';

// ── Emoji by keyword ──────────────────────────────────────────
const getEmoji = (name) => {
  const n = name.toLowerCase();
  if (n.includes('heritage') || n.includes('cultural') || n.includes('culture'))    return '🏛️';
  if (n.includes('eco') || n.includes('nature') || n.includes('environment'))        return '🌿';
  if (n.includes('hotel') || n.includes('hospitality') || n.includes('accommodat')) return '🏨';
  if (n.includes('tour operat') || n.includes('tour guid'))                          return '🗺️';
  if (n.includes('adventure') || n.includes('sport'))                                return '🧗';
  if (n.includes('digital') || n.includes('social media'))                           return '📱';
  if (n.includes('community') || n.includes('rural'))                                return '🤝';
  if (n.includes('food') || n.includes('culinary') || n.includes('restaurant'))     return '🍽️';
  if (n.includes('creative') || n.includes('art') || n.includes('craft'))           return '🎨';
  if (n.includes('business') || n.includes('enterprise') || n.includes('commerci')) return '💼';
  if (n.includes('destination') || n.includes('marketing'))                          return '🌍';
  if (n.includes('finance') || n.includes('investment') || n.includes('funding'))   return '💰';
  if (n.includes('travel') || n.includes('agency'))                                  return '✈️';
  if (n.includes('health') || n.includes('wellness'))                                return '🧘';
  if (n.includes('education') || n.includes('training'))                             return '🎓';
  if (n.includes('content') || n.includes('photo') || n.includes('media'))          return '📸';
  if (n.includes('relationship') || n.includes('customer') || n.includes('client')) return '🤝';
  if (n.includes('tech') || n.includes('innovation'))                                return '💡';
  return '🔷';
};

const COLORS = [
  { bg: 'bg-amber-50',  border: 'border-amber-200',  text: 'text-amber-900'  },
  { bg: 'bg-green-50',  border: 'border-green-200',  text: 'text-green-900'  },
  { bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-900'   },
  { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-900' },
  { bg: 'bg-pink-50',   border: 'border-pink-200',   text: 'text-pink-900'   },
  { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-900' },
  { bg: 'bg-teal-50',   border: 'border-teal-200',   text: 'text-teal-900'   },
  { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-900' },
  { bg: 'bg-cyan-50',   border: 'border-cyan-200',   text: 'text-cyan-900'   },
  { bg: 'bg-lime-50',   border: 'border-lime-200',   text: 'text-lime-900'   },
  { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-900' },
  { bg: 'bg-rose-50',   border: 'border-rose-200',   text: 'text-rose-900'   },
];

// ── Expert Card ───────────────────────────────────────────────
const ExpertCard = ({ expert }) => {
  const name     = `${expert.user?.firstName ?? ''} ${expert.user?.lastName ?? ''}`.trim();
  const initials = `${expert.user?.firstName?.[0] ?? ''}${expert.user?.lastName?.[0] ?? ''}`;
  const specs    = Array.isArray(expert.specializations)
    ? expert.specializations
    : (() => { try { return JSON.parse(expert.specializations || '[]'); } catch { return []; } })();
  const rate     = expert.hourlyRate
    ? `${expert.currency === 'USD' ? '$' : 'GH₵'}${expert.hourlyRate}/hr`
    : 'Rate on request';

  return (
    <div className="bg-white border border-cream-200 rounded-2xl p-5 hover:shadow-md hover:-translate-y-0.5 transition-all">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 bg-forest-700 rounded-xl flex items-center justify-center text-cream-50 font-serif font-bold flex-shrink-0 overflow-hidden">
          {expert.user?.avatar
            ? <img src={expert.user.avatar} alt={name} className="w-full h-full object-cover rounded-xl" />
            : initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-sans font-semibold text-forest-900 text-sm truncate">{name}</div>
          <div className="text-xs text-forest-500 truncate">{specs.slice(0, 2).join(' · ')}</div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="font-semibold text-forest-900 text-xs">{rate}</div>
        </div>
      </div>
      <div className="flex items-center justify-between pt-3 border-t border-cream-100">
        {expert.averageRating ? (
          <span className="flex items-center gap-1 text-xs text-forest-500">
            <Star size={11} className="fill-amber-400 text-amber-400" />
            {Number(expert.averageRating).toFixed(1)}
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs text-forest-400">
            <Clock size={11} /> {expert.yearsOfExperience} yrs exp
          </span>
        )}
        <Link to={`/experts/${expert.id}`} className="btn-primary text-xs py-1.5 px-3">Book</Link>
      </div>
    </div>
  );
};

const SkeletonCard = () => (
  <div className="bg-white border border-cream-200 rounded-2xl p-5 animate-pulse space-y-3">
    <div className="flex gap-3">
      <div className="w-11 h-11 bg-cream-200 rounded-xl flex-shrink-0" />
      <div className="flex-1 space-y-1.5 pt-1">
        <div className="h-3.5 bg-cream-200 rounded w-2/3" />
        <div className="h-3 bg-cream-100 rounded w-1/2" />
      </div>
    </div>
    <div className="h-px bg-cream-100" />
    <div className="flex justify-between">
      <div className="h-3 bg-cream-100 rounded w-20" />
      <div className="h-6 bg-cream-200 rounded-full w-12" />
    </div>
  </div>
);

const SkeletonCat = () => (
  <div className="bg-cream-100 border-2 border-cream-200 rounded-2xl p-5 animate-pulse space-y-3">
    <div className="w-8 h-8 bg-cream-200 rounded" />
    <div className="h-3.5 bg-cream-200 rounded w-3/4" />
    <div className="h-3 bg-cream-200 rounded w-1/3" />
  </div>
);

// ── Main Page ─────────────────────────────────────────────────
const CategoriesPage = () => {
  const [videoReady,      setVideoReady]      = useState(false);
  const [categories,      setCategories]      = useState([]);
  const [loadingCats,     setLoadingCats]     = useState(true);
  const [selected,        setSelected]        = useState(null);
  const [experts,         setExperts]         = useState([]);
  const [loadingExperts,  setLoadingExperts]  = useState(false);
  const [expertsError,    setExpertsError]    = useState(false);

  // ── Fetch real categories from backend ──────────────────────
  useEffect(() => {
    publicService.getSpecializations()
      .then(res => {
        const d = res.data?.data ?? res.data;
        setCategories(d?.specializations ?? []);
      })
      .catch(() => setCategories([]))
      .finally(() => setLoadingCats(false));
  }, []);

  // ── Fetch experts when category selected ───────────────────
  useEffect(() => {
    if (!selected) { setExperts([]); return; }
    setLoadingExperts(true);
    setExpertsError(false);
    expertService.browse({ search: selected.name, limit: 6, page: 1 })
      .then(res => {
        const d    = res.data?.data ?? res.data;
        const list = Array.isArray(d) ? d
          : Array.isArray(d?.experts) ? d.experts
          : Array.isArray(d?.data)    ? d.data : [];
        setExperts(list);
      })
      .catch(() => { setExpertsError(true); setExperts([]); })
      .finally(() => setLoadingExperts(false));
  }, [selected]);

  const handleSelect = (cat) =>
    setSelected(prev => prev?.name === cat.name ? null : cat);

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />

      {/* ── Video Hero ── */}
      <div className="relative" style={{ minHeight: 380 }}>
        <video
          autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
          style={{ zIndex: 0, opacity: videoReady ? 1 : 0 }}
          onCanPlay={() => setVideoReady(true)}
          onError={() => setVideoReady(true)}
        >
          <source src={VIDEO_URL} type="video/mp4" />
        </video>
        {!videoReady && <div className="absolute inset-0" style={{ zIndex: 0, background: '#0a1e0d' }} />}
        <div className="absolute inset-0"
          style={{ zIndex: 1, background: 'linear-gradient(160deg,rgba(2,10,5,.82) 0%,rgba(3,14,7,.78) 50%,rgba(1,8,3,.88) 100%)' }} />
        <div className="absolute top-0 left-0 right-0 h-[3px]"
          style={{ zIndex: 2, background: 'linear-gradient(90deg,#dc2626,#d97706,#16a34a,#d97706,#dc2626)' }} />

        <div className="relative z-10 pt-32 pb-20 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-widest mb-6"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              Explore Categories
            </div>
            <h1 className="font-serif text-4xl lg:text-6xl font-semibold text-white leading-tight mb-6">
              Find your <span className="italic font-light text-green-300">niche</span>
            </h1>
            <p className="text-white/55 text-lg leading-relaxed">
              Browse experts by specialization. Click any category to see matching experts.
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16"
          style={{ zIndex: 3, background: 'linear-gradient(to bottom,transparent,#f8f6f0)' }} />
      </div>

      {/* ── Categories grid — live from backend ── */}
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-12">
          {loadingCats
            ? Array.from({ length: 8 }).map((_, i) => <SkeletonCat key={i} />)
            : categories.map((cat, i) => {
                const color      = COLORS[i % COLORS.length];
                const isSelected = selected?.name === cat.name;
                return (
                  <button
                    key={cat.name}
                    onClick={() => handleSelect(cat)}
                    className={`group text-left border-2 rounded-2xl p-5 transition-all duration-200 hover:-translate-y-1 hover:shadow-md ${
                      isSelected
                        ? 'border-forest-600 bg-forest-50 shadow-md -translate-y-1'
                        : `${color.bg} ${color.border}`
                    }`}
                  >
                    <div className="text-3xl mb-3">{getEmoji(cat.name)}</div>
                    <h3 className="font-serif font-semibold text-forest-900 text-sm leading-snug mb-1">
                      {cat.name}
                    </h3>
                    <p className="text-xs text-forest-500 font-medium">
                      {cat.count} expert{cat.count !== 1 ? 's' : ''}
                    </p>
                    {isSelected && (
                      <p className="text-[10px] text-forest-600 font-semibold mt-2 uppercase tracking-widest">
                        Selected ✓
                      </p>
                    )}
                  </button>
                );
              })}
        </div>

        {/* Empty state */}
        {!loadingCats && categories.length === 0 && (
          <div className="text-center py-12 text-forest-400">
            <Users size={36} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No categories yet — check back as more experts join.</p>
            <Link to="/experts" className="btn-primary inline-flex mt-4">Browse All Experts</Link>
          </div>
        )}

        {/* ── Expert results ── */}
        {selected && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-serif text-2xl font-semibold text-forest-900 flex items-center gap-2">
                  {getEmoji(selected.name)} {selected.name}
                </h2>
                <p className="text-sm text-forest-500 mt-1">
                  Showing experts specialising in {selected.name}
                </p>
              </div>
              <Link
                to={`/experts?specialization=${encodeURIComponent(selected.name)}`}
                className="text-sm text-forest-600 hover:text-forest-900 font-medium border border-cream-200 hover:border-forest-400 px-4 py-2 rounded-full transition-all"
              >
                See all →
              </Link>
            </div>

            {expertsError && (
              <div className="flex items-center gap-3 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-5 py-4 mb-6">
                <AlertCircle size={16} />
                <span className="text-sm">Could not load experts — please try again.</span>
              </div>
            )}

            {!loadingExperts && !expertsError && experts.length === 0 && (
              <div className="text-center py-12 bg-white border border-cream-200 rounded-2xl text-forest-400">
                <Users size={32} className="mx-auto mb-3 opacity-30" />
                <p className="font-serif text-xl text-forest-600 mb-2">No experts found</p>
                <p className="text-sm mb-4">Try browsing all experts for this category.</p>
                <Link to={`/experts?specialization=${encodeURIComponent(selected.name)}`} className="btn-primary inline-flex">
                  Browse All Experts
                </Link>
              </div>
            )}

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {loadingExperts
                ? Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)
                : experts.map(e => <ExpertCard key={e.id} expert={e} />)}
            </div>
          </div>
        )}

        {!selected && !loadingCats && categories.length > 0 && (
          <div className="text-center py-8 text-forest-400">
            <p className="text-sm">Select a category above to browse matching experts</p>
          </div>
        )}
      </div>

      <CTA />
      <Footer />
    </div>
  );
};

export default CategoriesPage;